import { Link } from "react-router"
import {
  faCoins,
  faList,
  faSquareDollar,
} from "@fortawesome/pro-solid-svg-icons"
import cx from "classnames"

import ProjectCardStatusBadge from "../../../components/ProjectCard/ProjectCardStatusBadge"
import ProjectCardImage from "../../../components/ProjectCard/ProjectCardImage"
import ProjectCardName from "../../../components/ProjectCard/ProjectCardName"
import IconText from "../../../components/IconText"
import {
  getProjectBadgeType,
  getProjectBadgeText,
  getProjectType,
  getProjectEarningsNullValue,
} from "../../../shared/utils"
import { useAccountUrlPrefix } from "../../../hooks"
import { formatCurrency } from "../../../utils"
import { ProjectDataTypes } from "@/types/program"

export interface SavedProjectsStacksCardProps {
  className?: string
  programData: ProjectDataTypes
}

const SavedProjectsStacksCard = ({
  className,
  programData,
}: SavedProjectsStacksCardProps) => {
  const accountUrlPrefix = useAccountUrlPrefix()
  const badgeType = getProjectBadgeType(programData.badge_display)
  const badgeText = getProjectBadgeText(programData.badge_display)
  const projectType = getProjectType(programData.type, "short")

  return (
    <div
      className="project-card-container relative flex"
      data-testid="saved-projects-stacks-card"
    >
      <Link
        to={`${accountUrlPrefix}/programs/${programData.id}`}
        className={cx(
          "project-card",
          "flex flex-col rounded-sm bg-cloud-50 border border-solid border-dusk-50 p-4 select-none min-w-full",
          "transition-shadow duration-200 [transition-timing-function:cubic-bezier(0.4,0,0.2,1)]",
          "hover:cursor-pointer hover:shadow-md active:shadow-xs",
          programData?.is_active === false
            ? "outline-[#f9303f] outline-4 -outline-offset-4"
            : "",
          className
        )}
        aria-label={programData.name}
      >
        <ProjectCardStatusBadge
          showMinimalInfo={false}
          badgeType={badgeType}
          badgeText={badgeText}
        />

        <ProjectCardImage
          imageUrl={programData.image_url}
          isVerified={programData.is_verified}
          alt={programData.name}
        />

        <ProjectCardName name={programData.name} />

        <div className="grid grid-cols-[repeat(2,max-content)] sm:grid-cols-[repeat(3,max-content)] grid-rows-1 gap-2 mt-2 mb-4">
          <IconText
            icon={faCoins}
            className="text-sm text-dusk-700 leading-6 tracking-[0.01em]"
            iconClass="text-base leading-[1.50] tracking-[0.02em] mr-1"
          >
            {programData?.lo_cost === 0
              ? "No landowner cost"
              : `$${programData?.lo_cost}/per acre`}
          </IconText>

          <IconText
            icon={faList}
            className="text-sm text-dusk-700 leading-6 tracking-[0.01em]"
            iconClass="text-base leading-[1.50] tracking-[0.02em] mr-1"
          >
            {projectType}
          </IconText>

          <IconText
            icon={faSquareDollar}
            className="text-sm text-dusk-700 leading-6 tracking-[0.01em]"
            iconClass="text-lg leading-[1.50] tracking-[0.02em] mr-1"
          >
            {programData.potential_earnings === null ||
            programData.potential_earnings === undefined
              ? getProjectEarningsNullValue(programData.type)
              : `${formatCurrency(programData.potential_earnings)}/acre`}
          </IconText>
        </div>

        <p className="text-base leading-[130%] tracking-[0.02em]">
          {programData.description_short}
        </p>
      </Link>
    </div>
  )
}

export default SavedProjectsStacksCard
